import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import {
  MessageSquareQuote, Loader2, Check, X, Sparkles, Send, Trash2, Inbox, RefreshCw, CheckCircle2,
} from 'lucide-react';

const STATUS_LABEL = {
  extracting: 'AI is extracting the individual points…',
  drafting: 'AI is amending the §§ and drafting the reply…',
};

function RemarkRow({ r, idx, isAdmin, busy, onDecide }) {
  const [comment, setComment] = useState(r.comment || '');
  const decided = r.status !== 'open';
  return (
    <div className={`border rounded-sm px-3 py-2 ${r.status === 'accepted' ? 'border-emerald-500/40 bg-emerald-500/5'
      : r.status === 'rejected' ? 'border-red-500/40 bg-red-500/5' : 'border-border bg-secondary/20'}`}
      data-testid={`nda-remark-${idx}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs text-foreground font-medium">{r.topic}{r.section ? <span className="text-muted-foreground font-normal"> · {r.section}</span> : null}</p>
          {r.quote && <p className="text-[11px] text-muted-foreground italic mt-0.5">„{r.quote}"</p>}
          {r.suggestion && <p className="text-[11px] text-sky-300 mt-0.5">Vorschlag: {r.suggestion}</p>}
          {decided && r.comment && <p className="text-[11px] text-amber-300 mt-0.5">Unser Kommentar: {r.comment}</p>}
        </div>
        {decided ? (
          <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 uppercase shrink-0 ${
            r.status === 'accepted' ? 'border-emerald-500/40 text-emerald-300' : 'border-red-500/40 text-red-400'}`}
            data-testid={`nda-remark-status-${idx}`}>
            {r.status === 'accepted' ? 'Akzeptiert' : 'Abgelehnt'}
          </span>
        ) : null}
      </div>
      {isAdmin && (
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          <input value={comment} onChange={e => setComment(e.target.value)}
            placeholder="Eigener Kommentar / Gegenvorschlag (optional)…"
            data-testid={`nda-remark-comment-${idx}`}
            className="flex-1 min-w-[180px] bg-secondary border border-border rounded-sm text-[11px] px-2 h-7 text-foreground placeholder:text-muted-foreground/60" />
          <Button size="sm" disabled={busy} onClick={() => onDecide(r.id, 'accepted', comment)}
            data-testid={`nda-remark-accept-${idx}`}
            className={`h-7 px-2 text-[11px] rounded-sm ${r.status === 'accepted' ? 'bg-emerald-600 text-white' : 'bg-secondary border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/20'}`}>
            <Check className="w-3 h-3 mr-1" /> Annehmen
          </Button>
          <Button size="sm" disabled={busy} onClick={() => onDecide(r.id, 'rejected', comment)}
            data-testid={`nda-remark-reject-${idx}`}
            className={`h-7 px-2 text-[11px] rounded-sm ${r.status === 'rejected' ? 'bg-red-600 text-white' : 'bg-secondary border border-red-500/40 text-red-300 hover:bg-red-600/20'}`}>
            <X className="w-3 h-3 mr-1" /> Ablehnen
          </Button>
        </div>
      )}
    </div>
  );
}

export function NdaRemarksPanel({ projectId, isAdmin, hasNdas }) {
  const [chain, setChain] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [busy, setBusy] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sectionEdits, setSectionEdits] = useState({});
  const chainRef = useRef(null);
  chainRef.current = chain;

  const load = useCallback(() => {
    api.getNdaRemarks(projectId).then(r => {
      setChain(r.data.chain);
      setLoaded(true);
      if (r.data.chain?.status === 'drafted') {
        setReplyText(prev => prev || r.data.chain.reply_text || '');
      }
    }).catch(() => setLoaded(true));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!chain || !['extracting', 'drafting'].includes(chain.status)) return;
    const t = setInterval(() => {
      api.getNdaRemarks(projectId).then(r => {
        setChain(r.data.chain);
        if (r.data.chain?.status === 'drafted') setReplyText(r.data.chain.reply_text || '');
      }).catch(() => {});
    }, 3500);
    return () => clearInterval(t);
  }, [chain?.status, chain, projectId]);

  const doImport = async (auto) => {
    setBusy(true);
    try {
      const r = await api.importNdaRemarks(projectId, auto ? { auto: true } : { text: pasteText });
      setChain(r.data.chain);
      setImportOpen(false);
      setPasteText('');
      toast.success('Anmerkungen importiert — KI extrahiert die Punkte…');
    } catch (err) { toast.error(err.response?.data?.detail || 'Import fehlgeschlagen'); }
    setBusy(false);
  };

  const decide = async (rid, decision, comment) => {
    setBusy(true);
    try {
      const r = await api.decideNdaRemark(projectId, chain.id, rid, { decision, comment });
      setChain(r.data.chain);
      setReplyText('');
      setSectionEdits({});
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
    setBusy(false);
  };

  const draft = async () => {
    setBusy(true);
    try {
      await api.draftNdaRemarks(projectId, chain.id);
      setChain(c => ({ ...c, status: 'drafting', ai_error: '' }));
      setReplyText('');
      setSectionEdits({});
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
    setBusy(false);
  };

  const send = async () => {
    if (!window.confirm('Antwort + aktualisierte NDA-Version jetzt senden? Der alte Signatur-Link wird ungültig.')) return;
    setBusy(true);
    try {
      const amended = (chain.amended_sections || []).map(s => ({
        heading: s.heading,
        paragraphs: (sectionEdits[s.heading] ?? s.paragraphs.join('\n\n')).split(/\n\s*\n/).map(p => p.trim()).filter(Boolean),
      }));
      await api.updateNdaRemarks(projectId, chain.id, { reply_text: replyText, amended_sections: amended });
      const r = await api.sendNdaRemarks(projectId, chain.id, { origin: window.location.origin });
      setChain(r.data.chain);
      toast.success('Antwort + neue NDA-Version gesendet — im selben E-Mail-Verlauf');
    } catch (err) { toast.error(err.response?.data?.detail || 'Senden fehlgeschlagen'); }
    setBusy(false);
  };

  const discard = async () => {
    if (!window.confirm('Diese Anmerkungs-Kette verwerfen?')) return;
    try { await api.discardNdaRemarks(projectId, chain.id); setChain(null); setReplyText(''); setSectionEdits({}); }
    catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  if (!loaded || (!chain && !hasNdas)) return null;

  const openCount = (chain?.remarks || []).filter(r => r.status === 'open').length;
  const canDiscard = isAdmin && chain && ['open', 'drafted', 'error'].includes(chain.status);

  return (
    <div className="mt-3 border-t border-border/60 pt-3" data-testid="nda-remarks-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-foreground font-medium flex items-center gap-1.5">
          <MessageSquareQuote className="w-3.5 h-3.5 text-amber-400" /> Owner-Anmerkungen
          {chain?.status === 'sent' && (
            <span className="text-[9px] font-mono border border-sky-500/40 bg-sky-500/10 text-sky-300 rounded px-1.5 py-0.5 uppercase" data-testid="nda-remarks-chip-sent">
              Antwort + Rev. gesendet · wartet auf Unterschrift
            </span>
          )}
          {chain?.status === 'finalized' && (
            <span className="text-[9px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded px-1.5 py-0.5 uppercase flex items-center gap-1" data-testid="nda-remarks-chip-finalized">
              <CheckCircle2 className="w-3 h-3" /> Finalisiert — angepasste Version unterschrieben
            </span>
          )}
        </p>
        <span className="flex items-center gap-1.5">
          {canDiscard && (
            <button onClick={discard} title="Kette verwerfen" data-testid="nda-remarks-discard-btn"
              className="text-muted-foreground hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
          )}
          {isAdmin && (!chain || ['sent', 'finalized'].includes(chain.status)) && (
            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} data-testid="nda-remarks-import-btn"
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-sm text-[11px] h-7 px-2.5">
              <Inbox className="w-3 h-3 mr-1.5" /> Anmerkungen importieren
            </Button>
          )}
        </span>
      </div>
      {!chain && isAdmin && (
        <p className="text-[11px] text-muted-foreground mt-1">
          Hat der Inhaber Anmerkungen zum NDA geschickt? Importiere sie — die KI extrahiert jeden Punkt, du entscheidest einzeln, und Antwort + angepasste Version gehen in einer E-Mail raus.
        </p>
      )}
      {chain && ['extracting', 'drafting'].includes(chain.status) && (
        <p className="text-[11px] text-sky-300 mt-2 flex items-center gap-1.5" data-testid="nda-remarks-working">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> {STATUS_LABEL[chain.status]}
        </p>
      )}
      {chain?.status === 'error' && (
        <div className="mt-2 text-[11px] text-red-400 flex items-center gap-2" data-testid="nda-remarks-error">
          <span>KI-Fehler: {chain.ai_error}</span>
          {isAdmin && (chain.remarks || []).length > 0 && (
            <Button size="sm" variant="outline" onClick={draft} disabled={busy}
              className="border-border text-muted-foreground rounded-sm text-[10px] h-6 px-2">
              <RefreshCw className="w-3 h-3 mr-1" /> Erneut
            </Button>
          )}
        </div>
      )}
      {chain && ['open', 'drafted', 'error', 'sent', 'finalized'].includes(chain.status) && (chain.remarks || []).length > 0 && (
        <div className="space-y-1.5 mt-2">
          {chain.remarks.map((r, i) => (
            <RemarkRow key={r.id} r={r} idx={i} busy={busy} onDecide={decide}
              isAdmin={isAdmin && ['open', 'drafted', 'error'].includes(chain.status)} />
          ))}
        </div>
      )}
      {chain?.status === 'open' && openCount === 0 && isAdmin && (
        <Button size="sm" onClick={draft} disabled={busy} data-testid="nda-remarks-draft-btn"
          className="mt-2 bg-violet-600 hover:bg-violet-500 text-white rounded-sm text-xs h-8 px-3">
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Antwort & neue NDA-Version entwerfen (KI)
        </Button>
      )}
      {chain?.status === 'open' && openCount > 0 && (
        <p className="text-[10px] text-muted-foreground mt-1.5">{openCount} Anmerkung(en) noch offen — erst alle entscheiden, dann entwirft die KI Antwort & neue Version.</p>
      )}
      {chain?.status === 'drafted' && (
        <div className="mt-3 space-y-3" data-testid="nda-remarks-drafted">
          {(chain.amended_sections || []).length > 0 ? (
            <div className="space-y-2">
              <p className="text-[11px] text-violet-300 font-medium">Geänderte §§ (editierbar — neue Version Rev. {'>'}1):</p>
              {chain.amended_sections.map((s, i) => (
                <div key={s.heading} className="border border-violet-500/30 bg-violet-500/5 rounded-sm p-2">
                  <p className="text-[11px] text-foreground font-medium mb-1">{s.heading}</p>
                  <Textarea rows={4} disabled={!isAdmin} data-testid={`nda-amended-section-${i}`}
                    value={sectionEdits[s.heading] ?? s.paragraphs.join('\n\n')}
                    onChange={e => setSectionEdits(prev => ({ ...prev, [s.heading]: e.target.value }))}
                    className="bg-secondary border-border text-foreground rounded-sm text-[11px] font-mono leading-relaxed" />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">Keine §§-Änderungen (alle Punkte abgelehnt) — es geht nur die Antwort raus, mit erneutem Signatur-Link.</p>
          )}
          <div>
            <p className="text-[11px] text-sky-300 font-medium mb-1">Antwort-E-Mail (editierbar — geht als Reply im selben Verlauf raus, „[Vereinbarung ansehen & unterzeichnen]" wird zum Signatur-Button):</p>
            <Textarea rows={10} value={replyText} onChange={e => setReplyText(e.target.value)} disabled={!isAdmin}
              data-testid="nda-remarks-reply-textarea"
              className="bg-secondary border-border text-foreground rounded-sm text-[11px] leading-relaxed" />
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={send} disabled={busy || !replyText.trim()} data-testid="nda-remarks-send-btn"
                className="bg-teal-600 hover:bg-teal-500 text-white rounded-sm text-xs h-8 px-3">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Antwort + neue Version senden
              </Button>
              <Button size="sm" variant="outline" onClick={draft} disabled={busy} data-testid="nda-remarks-redraft-btn"
                className="border-border text-muted-foreground rounded-sm text-[11px] h-8 px-2.5">
                <RefreshCw className="w-3 h-3 mr-1" /> Neu entwerfen
              </Button>
            </div>
          )}
        </div>
      )}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-xl" data-testid="nda-remarks-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground flex items-center gap-2">
              <MessageSquareQuote className="w-4 h-4 text-amber-400" /> Owner-Anmerkungen importieren
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Button size="sm" variant="outline" onClick={() => doImport(true)} disabled={busy}
              data-testid="nda-remarks-auto-import-btn"
              className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10 rounded-sm text-xs h-8 w-full">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Inbox className="w-3.5 h-3.5 mr-1.5" />}
              Automatisch aus der E-Mail-Korrespondenz erkennen
            </Button>
            <p className="text-center text-[10px] text-muted-foreground">— oder E-Mail-Text einfügen —</p>
            <Textarea rows={10} value={pasteText} onChange={e => setPasteText(e.target.value)}
              placeholder={'Hallo Herr …,\n\nich habe hierzu 2 Anmerkungen mit der Bitte ggfs. um Anpassung: …'}
              data-testid="nda-remarks-paste-textarea"
              className="bg-secondary border-border text-foreground rounded-sm text-xs leading-relaxed" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setImportOpen(false)} disabled={busy}
                className="border-border text-muted-foreground rounded-sm text-xs h-8">Abbrechen</Button>
              <Button size="sm" onClick={() => doImport(false)} disabled={busy || pasteText.trim().length < 30}
                data-testid="nda-remarks-extract-btn"
                className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm text-xs h-8 px-3">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                Punkte extrahieren (KI)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
